import { useEffect } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonicalUrl?: string;
}

export default function SEO({
  title = "Dubai Motor City Property Investment | High ROI Dubai Apartments | 10% Rental Income | Golden Visa",
  description = "Invest in Dubai Motor City luxury apartments from $250K with 10% rental income guaranteed. Flexible payment plans, tax-free property, Golden Visa eligible. Best rental yield Dubai for international investors.",
  keywords = "Dubai real estate investment, Motor City Dubai property, high ROI Dubai apartments, best rental yield Dubai, Dubai property investment for foreigners, invest in Dubai from India, invest in Dubai from Pakistan, invest in Dubai from Turkey, invest in Dubai from UK, invest in Dubai from Germany, invest in Dubai from Canada, Dubai Golden Visa property, USD investment Dubai, 10% rental income Dubai, affordable Dubai property, Dubai Motor City apartments, Dubai property payment plan, Dubai real estate 2024, Dubai investment opportunity, Dubai property for sale, expat property Dubai, Dubai residential investment, Dubai apartment investment, tax-free property Dubai, Dubai property market, international property investment Dubai",
  ogImage = "/images/exterior-01.jpg",
  canonicalUrl = "https://www.suitedubaiestate.com"
}: SEOProps) {
  useEffect(() => {
    // Set document title
    document.title = title;

    // Set or update meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute("content", content);
    };

    // Basic meta tags
    setMetaTag("description", description);
    setMetaTag("keywords", keywords);
    setMetaTag("author", "Suite Global Estate");
    setMetaTag("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    setMetaTag("googlebot", "index, follow");
    setMetaTag("bingbot", "index, follow");
    
    // Geo-targeting
    setMetaTag("geo.region", "AE-DU");
    setMetaTag("geo.placename", "Dubai");
    setMetaTag("geo.position", "25.0657;55.1713");
    setMetaTag("ICBM", "25.0657, 55.1713");
    
    // Language and content
    setMetaTag("language", "English");
    setMetaTag("content-language", "en");

    // Open Graph tags
    setMetaTag("og:title", title, true);
    setMetaTag("og:description", description, true);
    setMetaTag("og:image", `${canonicalUrl}${ogImage}`, true);
    setMetaTag("og:image:width", "1200", true);
    setMetaTag("og:image:height", "630", true);
    setMetaTag("og:type", "website", true);
    setMetaTag("og:url", canonicalUrl, true);
    setMetaTag("og:site_name", "Motor City Dubai Investment", true);
    setMetaTag("og:locale", "en_US", true);

    // Twitter Card tags
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);
    setMetaTag("twitter:image", `${canonicalUrl}${ogImage}`);
    setMetaTag("twitter:site", "@SuiteGlobalEstate");

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // Multiple Structured Data schemas for comprehensive SEO
    const structuredDataArray = [
      // 1. Real Estate Listing
      {
        "@context": "https://schema.org",
        "@type": "RealEstateListing",
        "name": "Motor City Dubai Property - High ROI Dubai Apartments for International Investment",
        "description": description,
        "url": canonicalUrl,
        "image": [
          `${canonicalUrl}/images/exterior-01.jpg`,
          `${canonicalUrl}/images/exterior-02.jpg`,
          `${canonicalUrl}/images/interior-01.jpg`
        ],
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Al Qudra Road & Mohammed Bin Zayed Road",
          "addressLocality": "Motor City",
          "addressRegion": "Dubai",
          "postalCode": "00000",
          "addressCountry": "AE"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "25.0657",
          "longitude": "55.1713"
        },
        "offers": {
          "@type": "AggregateOffer",
          "lowPrice": "250000",
          "highPrice": "440000",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "priceValidUntil": "2027-12-31",
          "offerCount": "47"
        },
        "numberOfRooms": "1-2",
        "numberOfBathroomsTotal": "1-2",
        "floorSize": {
          "@type": "QuantitativeValue",
          "value": "50-90",
          "unitCode": "MTK"
        },
        "amenityFeature": [
          { "@type": "LocationFeatureSpecification", "name": "Swimming Pool" },
          { "@type": "LocationFeatureSpecification", "name": "Fitness Center" },
          { "@type": "LocationFeatureSpecification", "name": "24/7 Security" },
          { "@type": "LocationFeatureSpecification", "name": "SPA" },
          { "@type": "LocationFeatureSpecification", "name": "Playground" },
          { "@type": "LocationFeatureSpecification", "name": "Restaurant & Cafe" },
          { "@type": "LocationFeatureSpecification", "name": "Concierge Service" },
          { "@type": "LocationFeatureSpecification", "name": "Communal Garden" }
        ],
        "yearBuilt": "2027",
        "datePosted": "2024-11-22"
      },
      
      // 2. Organization (Suite Global Estate)
      {
        "@context": "https://schema.org",
        "@type": "RealEstateAgent",
        "name": "Suite Global Estate",
        "description": "Leading international real estate investment company specializing in Dubai property investment for foreigners, high ROI Dubai apartments, and tax-free property opportunities with 500+ satisfied investors worldwide.",
        "url": canonicalUrl,
        "logo": `${canonicalUrl}/logo.svg`,
        "telephone": "+49-176-28205172",
        "email": "info@suitedubaiestate.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Dubai",
          "addressCountry": "AE"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "500",
          "bestRating": "5"
        },
        "areaServed": [
          "India", "Pakistan", "Turkey", "United Kingdom", "Germany", "Canada", 
          "United States", "Saudi Arabia", "Russia", "China", "Brazil", "Italy"
        ]
      },
      
      // 3. Investment Product
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "Motor City Dubai Investment Property - Affordable Dubai Property with Best Rental Yield",
        "description": "High-yield Dubai real estate investment with target 10% rental income Dubai, flexible payment plans, tax-free property status, and Dubai Golden Visa eligibility for international investors.",
        "image": `${canonicalUrl}/images/exterior-01.jpg`,
        "brand": {
          "@type": "Brand",
          "name": "Suite Global Estate"
        },
        "offers": {
          "@type": "AggregateOffer",
          "lowPrice": "250000",
          "highPrice": "440000",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "url": canonicalUrl
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "500"
        }
      },
      
      // 4. FAQ Schema for Featured Snippets
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How much does it cost to invest in Dubai Motor City property for foreigners?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Motor City Dubai apartments start from $250,000 for a 1+1 unit (50m²) and $440,000 for a 2+1 unit (90m²). This affordable Dubai property requires only 20% down payment with flexible 3-year Dubai property payment plan."
            }
          },
          {
            "@type": "Question",
            "name": "What is the best rental yield in Dubai Motor City?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Motor City Dubai properties target 10% rental income Dubai based on historical averages in similar Suite Global Estate projects. This high ROI Dubai apartment offers USD-based income and professional property management."
            }
          },
          {
            "@type": "Question",
            "name": "Can foreigners buy property in Dubai Motor City? Is Dubai property investment for foreigners safe?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, foreigners have 100% freehold ownership rights in Dubai Motor City. No restrictions on nationality. Investors looking to invest in Dubai from India, Pakistan, Turkey, UK, Germany, Canada and worldwide can safely purchase this expat property Dubai."
            }
          },
          {
            "@type": "Question",
            "name": "Is Dubai Motor City property eligible for Dubai Golden Visa?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, property purchases above AED 2,000,000 ($545,000) qualify for UAE's 10-year Dubai Golden Visa. This Dubai property for sale offers a path to residency for international investors."
            }
          },
          {
            "@type": "Question",
            "name": "When will Motor City Dubai property be ready for handover?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Motor City Dubai apartments will be completed and ready for handover in December 2027. This Dubai real estate 2024 project is on schedule with a reputable developer."
            }
          },
          {
            "@type": "Question",
            "name": "Is Dubai property tax-free? What are the costs of a tax-free property Dubai investment?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No, Dubai has 0% property tax, 0% income tax, and 0% capital gains tax. This tax-free property Dubai status makes it one of the most tax-efficient Dubai real estate investment destinations globally for international property investment Dubai."
            }
          }
        ]
      },
      
      // 5. Breadcrumb Navigation
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": canonicalUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Dubai Real Estate",
            "item": `${canonicalUrl}#properties`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Motor City Investment",
            "item": canonicalUrl
          }
        ]
      },
      
      // 6. WebSite Schema for Sitelinks Search Box
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Motor City Dubai Investment",
        "url": canonicalUrl,
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${canonicalUrl}?q={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      }
    ];

    // Remove old script tags
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    
    // Add all structured data schemas
    structuredDataArray.forEach(data => {
      const scriptTag = document.createElement("script");
      scriptTag.setAttribute("type", "application/ld+json");
      scriptTag.textContent = JSON.stringify(data);
      document.head.appendChild(scriptTag);
    });

  }, [title, description, keywords, ogImage, canonicalUrl]);

  return null;
}
